import has from 'lodash/has';
import isNaN from 'lodash/isNaN';
import isString from 'lodash/isString';
import sortBy from 'lodash/sortBy';
import toNumber from 'lodash/toNumber';
import cloneDeep from 'lodash/cloneDeep';
import intersection from 'lodash/intersection';
import type { LocationQuery } from 'vue-router';
import { Directory, Filesystem } from '@capacitor/filesystem';
import {
  ExternalProviderEnum,
  axenitaRequiredParams,
  SMRPatientDataJSONFieldKeysEnum,
  SMRRequiredParams,
  SMRNumericParams,
  wePracticeRequiredParams,
  CGMPhoenixRequiredParams
} from '~/utils/constants/session';
import type { PatientSessionDetails, Recording, Document } from '~/api/generated';

const isRecording = (documentOrRecording: Recording | Document): documentOrRecording is Recording =>
  'recording_source' in documentOrRecording;

const composeSMRQueryParams = (query: LocationQuery) => {
  const params: Record<string, string | number> = {};

  Object.entries(query).forEach(([key, value]) => {
    if (!value || !isString(value)) return;

    if (SMRNumericParams.includes(key as SMRPatientDataJSONFieldKeysEnum)) {
      params[key] = toNumber(value);
    } else {
      params[key] = value;
    }
  });

  return params as {
    [key in SMRPatientDataJSONFieldKeysEnum]: string | number;
  };
};

const requiredParamsByProvider: Record<string, ReadonlyArray<string>> = {
  [ExternalProviderEnum.axenita]: axenitaRequiredParams,
  [ExternalProviderEnum.wepractice]: wePracticeRequiredParams,
  [ExternalProviderEnum.smr]: SMRRequiredParams,
  [ExternalProviderEnum.cgm_phoenix]: CGMPhoenixRequiredParams
};

const validateExternalSessionQuery = (query: Record<string, string> | LocationQuery) => {
  const companySlug = query[ExternalProviderEnum.company_slug];

  if (!isString(companySlug) || !has(ExternalProviderEnum, companySlug)) return false;

  const requiredParams = requiredParamsByProvider[companySlug];

  if (!requiredParams) return false;

  return intersection(Object.keys(query), requiredParams).length === requiredParams.length;
};

async function getExpiredSessionIds() {
  const sessionStore = useSessionStore();

  try {
    const sessionsIds = new Set(sessionStore.sessions.map(({ id }) => id));
    const { files: sessionsDirs } = await Filesystem.readdir({
      path: getSessionsDirectory(),
      directory: Directory.Documents
    });

    return sessionsDirs.map(({ name }) => toNumber(name)).filter((id) => !isNaN(id) && !sessionsIds.has(id));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to get expired session IDs:', err);
    return [];
  }
}

// Recording stopwatch (b-recording.vue) and report data (s-session.vue)
const removeSessionStorage = (sessionId: PatientSessionDetails['id']) => {
  localStorage.removeItem(`session-${sessionId}`);
  localStorage.removeItem(`session-${sessionId}-data`);
};

const sessionStorageKeyPattern = /^session-(\d+)(?:-data)?$/;

// Sessions that are opened but never recorded or deleted leave their keys behind, so drop everything
// that no longer belongs to an existing session before it fills up the localStorage quota
const removeOrphanedSessionsStorage = (sessionIds: Array<PatientSessionDetails['id']>) => {
  const existingIds = new Set(sessionIds);

  Object.keys(localStorage)
    .filter((key) => {
      const id = toNumber(key.match(sessionStorageKeyPattern)?.[1]);
      return !isNaN(id) && !existingIds.has(id);
    })
    .forEach((key) => localStorage.removeItem(key));
};

const composeSession = (session: PatientSessionDetails) => {
  const _session = cloneDeep(session);

  return {
    ..._session,
    recordings: sortBy(_session.recordings, 'sequence_number')
  };
};

export {
  validateExternalSessionQuery,
  getExpiredSessionIds,
  removeSessionStorage,
  removeOrphanedSessionsStorage,
  composeSession,
  composeSMRQueryParams,
  isRecording
};
